import { Component, effect, inject, input, ChangeDetectionStrategy } from '@angular/core';

import { combineLatest, Observable } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { AsyncPipe } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  AppleProfileStoreActions,
  CategoryConfigurationInheritedStoreSelectors,
  CategoryStoreSelectors,
  DeviceStoreActions,
  DeviceStoreSelectors,
} from '@era/stores';
import { AppleApplicationLockChanged, Device, isNotUndefined } from '@rocksoft/shared';
import { AppleDeviceComponent } from './apple-device.component';
import { AppleDeviceService } from './apple-device.service';

@Component({
  selector: 'era-apple-device',
  templateUrl: './apple-device.container.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AppleDeviceComponent, AsyncPipe],
})
export class AppleDeviceContainerComponent {
  private readonly deviceStore = inject(Store);
  private readonly appleDeviceService = inject(AppleDeviceService);

  readonly udid = input.required<string>();
  public readonly device$: Observable<Device>;

  constructor() {
    const udid$ = toObservable(this.udid);
    const device$ = udid$.pipe(
      switchMap((udid) => this.deviceStore.select(DeviceStoreSelectors.selectDeviceByUdid(udid)))
    );
    const definedDevice$: Observable<Device> = device$.pipe(filter(isNotUndefined));
    const category$ = definedDevice$.pipe(
      switchMap((device) => this.deviceStore.select(CategoryStoreSelectors.selectCategoryByDevice(device))),
      filter(isNotUndefined)
    );

    this.device$ = combineLatest([category$, definedDevice$]).pipe(
      switchMap(([category, device]) =>
        this.deviceStore.select(
          CategoryConfigurationInheritedStoreSelectors.selectApplicationsForDeviceWithConfigurationByCategoryId(
            category.categoryId,
            device
          )
        )
      ),
      filter(isNotUndefined),
      switchMap((device) => this.appleDeviceService.observeAppleDeviceWithLockedFlag(device))
    );

    effect(() => {
      const udid = this.udid();
      if (!udid) return;

      this.deviceStore.dispatch(DeviceStoreActions.DeviceResolverActions.appleDeviceRequested({ udid: udid }));
      this.deviceStore.dispatch(AppleProfileStoreActions.AppleDeviceContainerActions.profilesRequested({ udid: udid }));
    });
  }

  public applicationLockChanged(event: AppleApplicationLockChanged) {
    this.deviceStore.dispatch(
      AppleProfileStoreActions.AppleDeviceContainerActions.singleAppModeChanged({
        udid: event.udid,
        bundleIdentifier: event.bundleIdentifier,
        locked: event.locked,
      })
    );
  }
}
